/**
 * ESG SaaS Platform - Audit Trail API Service
 *
 * API service for accessing audit logs (Admin only).
 */

import { api } from './api';

// Types
export interface AuditLogEntry {
  id: string;
  timestamp: string;
  user_email: string;
  user_name?: string;
  user_role?: string;
  action: string;
  entity_type: string;
  entity_id?: string;
  description?: string;
  changes?: Record<string, any>;
  ip_address?: string;
}

export interface AuditLogDetail extends AuditLogEntry {
  previous_values?: Record<string, any>;
  new_values?: Record<string, any>;
  user_agent?: string;
  request_id?: string;
  endpoint?: string;
  http_method?: string;
  extra_data?: Record<string, any>;
}

export interface AuditLogListResponse {
  logs: AuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditStatistics {
  total_actions: number;
  actions_by_type: Record<string, number>;
  actions_by_entity: Record<string, number>;
  most_active_users: { email: string; name?: string; action_count: number }[];
  recent_activity_trend: { date: string; count: number }[];
}

export interface EntityHistory {
  entity_type: string;
  entity_id: string;
  history: AuditLogEntry[];
  count: number;
}

export interface UserActivity {
  user_email: string;
  period_days: number;
  total_actions: number;
  actions_summary: Record<string, number>;
  activity: AuditLogEntry[];
}

// Query parameters
interface ListParams {
  action?: string;
  entity_type?: string;
  entity_id?: string;
  user_email?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// Service functions
export const auditService = {
  /**
   * List audit logs with optional filters (Admin/Contributor only).
   */
  listLogs: (params: ListParams = {}) => {
    const query = new URLSearchParams();
    if (params.action) query.append('action', params.action);
    if (params.entity_type) query.append('entity_type', params.entity_type);
    if (params.entity_id) query.append('entity_id', params.entity_id);
    if (params.user_email) query.append('user_email', params.user_email);
    if (params.start_date) query.append('start_date', params.start_date);
    if (params.end_date) query.append('end_date', params.end_date);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());
    const queryString = query.toString();
    return api.get<AuditLogListResponse>(`/audit${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get detailed information about a specific audit log entry.
   */
  getLog: (logId: string) =>
    api.get<AuditLogDetail>(`/audit/${logId}`),

  /**
   * Get audit statistics for the specified time period (Admin only).
   */
  getStatistics: (days: number = 30) =>
    api.get<AuditStatistics>(`/audit/statistics?days=${days}`),

  /**
   * Get the complete audit history for a specific entity.
   */
  getEntityHistory: (entityType: string, entityId: string, limit: number = 50) =>
    api.get<EntityHistory>(`/audit/entity/${entityType}/${entityId}?limit=${limit}`),

  /**
   * Get activity history for a specific user (Admin only).
   */
  getUserActivity: (userEmail: string, days: number = 30, limit: number = 100) =>
    api.get<UserActivity>(`/audit/user/${encodeURIComponent(userEmail)}/activity?days=${days}&limit=${limit}`),
};
